import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import {
  getScrollTop,
  getScrollEventTarget,
  scrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const DENSITY_STORAGE_KEY = 'stz-collection-grid-density';
const BACK_TO_TOP_THRESHOLD = 400;

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {HTMLInputElement | null} */
  #searchInput = null;
  /** @type {HTMLElement | null} */
  #searchStatus = null;
  /** @type {MutationObserver | null} */
  #searchGridObserver = null;
  /** @type {HTMLButtonElement | null} */
  #backToTop = null;
  /** @type {EventTarget | null} */
  #backToTopScrollTarget = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    try {
      this.#bindResultsSearch();
    } catch (error) {
      console.error('Results search could not initialize', error);
    }
    try {
      this.#bindFacetGroupState();
    } catch (error) {
      console.error('Facet state could not initialize', error);
    }
    try {
      this.#bindBackToTop();
    } catch (error) {
      console.error('Back to top could not initialize', error);
    }
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#searchInput?.removeEventListener('input', this.#onResultsSearchInput);
    this.#searchInput?.removeEventListener('search', this.#onResultsSearchInput);
    this.#searchGridObserver?.disconnect();
    this.#searchGridObserver = null;
    this.#backToTop?.removeEventListener('click', this.#onBackToTopClick);
    this.#backToTopScrollTarget?.removeEventListener('scroll', this.#onBackToTopScroll);
    this.#backToTopScrollTarget = null;
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBackToTopBreakpointChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const density = this.#normalizeDensity(value);
      if (density) {
        window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
        return;
      }

      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    const density = this.#normalizeDensity(value);
    if (density) {
      grid.setAttribute('data-stz-density', density);
      grid.setAttribute('product-grid-view', `columns-${density}`);
      return;
    }

    grid.setAttribute('product-grid-view', value);
  }

  /**
   * @param {string} value
   * @returns {'3' | '4' | '5' | null}
   */
  #normalizeDensity(value) {
    return value === '3' || value === '4' || value === '5' ? value : null;
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    if (!event.matches) {
      const targetElement = this.querySelector('[data-grid-layout="mobile-option"]');
      if (!(targetElement instanceof HTMLInputElement)) return;
      targetElement.checked = true;
      this.#setLayout('default');
      return;
    }

    const storedDensity = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    const density = this.#normalizeDensity(storedDensity || '3') || '3';
    const targetElement = this.querySelector(`input[type="radio"][name="grid"][value="${density}"]`);
    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }
    this.#setLayout(density);
  };

  #bindResultsSearch() {
    this.#searchInput = this.querySelector('[data-stz-results-search]');
    this.#searchStatus = this.querySelector('[data-stz-results-search-status]');
    if (!(this.#searchInput instanceof HTMLInputElement)) return;

    this.#searchInput.addEventListener('input', this.#onResultsSearchInput);
    this.#searchInput.addEventListener('search', this.#onResultsSearchInput);
    this.#observeSearchGrid();
    this.#onResultsSearchInput();
  }

  /**
   * PaginatedList appends/prepends page cards onto `this.refs.grid` in private methods.
   * Re-apply the active within-results query whenever that grid's children change.
   */
  #observeSearchGrid() {
    this.#searchGridObserver?.disconnect();
    this.#searchGridObserver = null;

    const grid = this.#searchGrid();
    if (!(grid instanceof HTMLElement)) return;

    this.#searchGridObserver = new MutationObserver((mutations) => {
      const childListChanged = mutations.some(
        (mutation) => mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0),
      );
      if (!childListChanged) return;
      if (this.#activeSearchQuery().length === 0) return;
      this.#onResultsSearchInput();
    });
    this.#searchGridObserver.observe(grid, { childList: true });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #searchGrid() {
    const fromRefs = this.refs?.grid;
    if (fromRefs instanceof HTMLElement) return fromRefs;
    const fromAttr = this.querySelector('[ref="grid"]');
    return fromAttr instanceof HTMLElement ? fromAttr : null;
  }

  /**
   * @returns {string}
   */
  #activeSearchQuery() {
    return this.#searchInput instanceof HTMLInputElement ? this.#searchInput.value.trim().toLowerCase() : '';
  }

  #onResultsSearchInput = () => {
    const query = this.#activeSearchQuery();
    const cards = this.querySelectorAll('.product-grid__item[data-product-title]');
    let visible = 0;

    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const title = (card.getAttribute('data-product-title') || '').toLowerCase();
      const match = query.length === 0 || title.includes(query);
      card.hidden = !match;
      if (match) visible += 1;
    });

    if (this.#searchStatus instanceof HTMLElement) {
      this.#searchStatus.textContent = query.length === 0
        ? ''
        : `${visible} matching ${visible === 1 ? 'result' : 'results'}`;
    }
  };

  #bindFacetGroupState() {
    const groups = this.querySelectorAll('[data-stz-refine-rail] details.facets__panel');
    groups.forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      const summary = details.querySelector('summary');
      if (!(summary instanceof HTMLElement)) return;

      const panel = details.querySelector('.facets__panel-content, floating-panel-component');
      if (panel instanceof HTMLElement && panel.id) {
        summary.setAttribute('aria-controls', panel.id);
      }

      const hasActive = Boolean(
        details.querySelector('input:checked') ||
          details.querySelector('input[type="number"]:not([value=""]):not([value="0"])'),
      );
      if (!hasActive && !details.dataset.stzUserToggled) {
        details.open = false;
      }

      const sync = () => {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        summary.setAttribute('data-stz-facet-toggle', details.open ? 'minus' : 'plus');
      };

      sync();
      details.addEventListener('toggle', () => {
        details.dataset.stzUserToggled = 'true';
        sync();
      });
    });
  }

  #bindBackToTop() {
    const button = this.querySelector('[data-stz-back-to-top]');
    if (!(button instanceof HTMLButtonElement)) return;

    this.#backToTop = button;
    button.addEventListener('click', this.#onBackToTopClick);
    this.#bindBackToTopScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBackToTopBreakpointChange);
    this.#onBackToTopScroll();
  }

  /**
   * Desktop (≥990px) scrolls `.page-wrapper`; mobile uses the document root.
   * Rebind whenever that container swaps so squeeze and document scrolling both stay correct.
   */
  #bindBackToTopScrollTarget() {
    this.#backToTopScrollTarget?.removeEventListener('scroll', this.#onBackToTopScroll);
    this.#backToTopScrollTarget = getScrollEventTarget();
    this.#backToTopScrollTarget.addEventListener('scroll', this.#onBackToTopScroll, { passive: true });
  }

  #handleBackToTopBreakpointChange = () => {
    this.#bindBackToTopScrollTarget();
    this.#onBackToTopScroll();
  };

  #onBackToTopScroll = () => {
    if (!(this.#backToTop instanceof HTMLButtonElement)) return;
    const threshold = Number(this.#backToTop.dataset.stzBackToTopThreshold) || BACK_TO_TOP_THRESHOLD;
    this.#backToTop.toggleAttribute('hidden', getScrollTop() < threshold);
  };

  #onBackToTopClick = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollTo({
      top: 0,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.#onBackToTopScroll();
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
